/**
 * frontend/src/pages/CorpusPage.js
 * Paginated verse listing. Route: /browse/:catId/:subId
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getCategoryById, getSubcategoryById } from '../data/taxonomy';
import ApiService from '../services/ApiService';
import VerseCard  from '../components/verse/VerseCard';
import styles from './CorpusPage.module.css';

const PAGE_SIZE = 20;

export default function CorpusPage() {
  const { catId, subId } = useParams();
  const navigate         = useNavigate();
  const category         = getCategoryById(catId);
  const subcategory      = getSubcategoryById(catId, subId);

  const [verses,  setVerses]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [cursor,  setCursor]  = useState(null);
  const [total,   setTotal]   = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const primarySource = subcategory?.sourceCodes?.[0] || '';

  const loadVerses = useCallback(async (reset = false) => {
    if (!primarySource) return;
    setLoading(true);
    setError(null);
    try {
      if (reset && total === null) {
        ApiService.getSourceCount(primarySource).then(r => setTotal(r.count)).catch(() => {});
      }
      const result = await ApiService.listSourceVerses(primarySource, PAGE_SIZE, reset ? null : cursor);
      setVerses(prev => reset ? result.verses : [...prev, ...result.verses]);
      setCursor(result.cursor || null);
      setHasMore(result.has_more);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [primarySource, cursor, total]); // eslint-disable-line

  useEffect(() => {
    setVerses([]); setCursor(null); setHasMore(true); setTotal(null);
    loadVerses(true);
  }, [primarySource]); // eslint-disable-line

  if (!category || !subcategory) {
    return (
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>
        <p className={styles.notFound}>Subcategory not found.</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.hero} style={{ '--cat-color': category.color, '--cat-color-light': category.colorLight }}>
        <button className={styles.backBtn} onClick={() => navigate(`/browse/${catId}`)}>← {category.labelIast}</button>
        <div className={styles.heroText}>
          <h1 className={styles.heroDevanagari}>{subcategory.label}</h1>
          <p className={styles.heroIast}>{subcategory.labelIast}</p>
          {subcategory.description && <p className={styles.heroDesc}>{subcategory.description}</p>}
          {total !== null && <p className={styles.heroCount}>{total.toLocaleString()} verses</p>}
        </div>
        <div className={styles.heroBadges}>
          <span className={styles.badgeAvailable}>Available</span>
          {subcategory.shankara && <span className={styles.badgeShankara}>Śaṅkara Bhāṣya</span>}
        </div>
      </div>

      <div className={styles.content}>
        {error && <div className={styles.errorMsg}>Could not load verses: {error}</div>}
        {verses.length === 0 && !loading && !error && <p className={styles.emptyMsg}>No verses found for this source.</p>}
        <div className={styles.verseList}>
          {verses.map((verse) => (
            <Link key={verse.verse_id} to={`/verse/${encodeURIComponent(verse.verse_id)}`} className={styles.verseLink}>
              <VerseCard verse={verse} />
            </Link>
          ))}
        </div>
        {loading && <div className={styles.loadingRow}><span className={styles.spinner}>⟳</span></div>}
        {hasMore && !loading && verses.length > 0 && (
          <button className={styles.loadMoreBtn} onClick={() => loadVerses(false)}>Load more</button>
        )}
        {!hasMore && verses.length > 0 && <p className={styles.endMsg}>— {total !== null ? total.toLocaleString() : verses.length} verses total —</p>}
      </div>
    </div>
  );
}


